import React, { useState, useEffect } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { uploadImage } from "../../utils/helpers/fileupload";
import { getSubjectTeacher } from "../../store/slices/subjectteacher";
import { addHomework } from "../../store/slices/homework";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";

const requiredMessages = {
  standardId: "Please select standard",
  sectionId: "Please select section",
  subjectId: "Please select subject",
  dueDate: "Please select due date",
  title: "Please enter title",
  description: "Please enter description",
};

export default function AddHomework() {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;
  const teacherId = user?.data?.teacherId;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [rawData, setRawData] = useState([]);
  const [standards, setStandards] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [errors, setErrors] = useState({});
  const [file, setFile] = useState([]);



  useEffect(() => {
    const fetchData = async () => {
      const res = await dispatch(getSubjectTeacher({ teacherId })).unwrap();
      const data = res.data || [];

      setRawData(data);

      // unique standards
      const uniqueStandards = [
        ...new Map(
          data.map((item) => [item.standardId._id, item.standardId]),
        ).values(),
      ];

      setStandards(uniqueStandards);
    };

    fetchData();
  }, [dispatch, teacherId]);

  const [formData, setFormData] = useState({
    standardId: "",
    sectionId: "",
    subjectId: "",
    dueDate: "",
    title: "",
    description: "",
  });

  const handleStandardChange = (e) => {
    const standardId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      standardId,
      sectionId: "",
      subjectId: "",
    }));

    // Clear errors
    setErrors((prev) => ({
      ...prev,
      standardId: "",
      sectionId: "",
      subjectId: "",
    }));

    const filteredSections = rawData
      .filter((item) => item.standardId._id === standardId)
      .map((item) => item.sectionId);

    const uniqueSections = [
      ...new Map(filteredSections.map((sec) => [sec._id, sec])).values(),
    ];

    setSections(uniqueSections);
    setSubjects([]);
  };

  const handleSectionChange = (e) => {
    const sectionId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      sectionId,
      subjectId: "",
    }));

    // Clear errors
    setErrors((prev) => ({
      ...prev,
      sectionId: "",
      subjectId: "",
    }));

    const filteredSubjects = rawData
      .filter(
        (item) =>
          item.standardId._id === formData.standardId &&
          item.sectionId._id === sectionId
      )
      .map((item) => item.subjectId);

    const uniqueSubjects = [
      ...new Map(filteredSubjects.map((sub) => [sub._id, sub])).values(),
    ];

    setSubjects(uniqueSubjects);
  };

  const validateForm = () => {
    let newErrors = {};

    // required text/select fields
    Object.keys(requiredMessages).forEach((key) => {
      if (key === "file") return;

      if (!formData[key] || formData[key].toString().trim() === "") {
        newErrors[key] = requiredMessages[key];
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);


    if (file.length + selectedFiles.length > 3) {
      toast.error("You can upload maximum 3 files only");
      e.target.value = "";
      return;
    }

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    for (let f of selectedFiles) {
      if (!allowedTypes.includes(f.type)) {
        toast.error("Only PDF, JPG, PNG files allowed");
        e.target.value = "";
        return;
      }

      if (f.size > 10 * 1024 * 1024) {
        toast.error("Each file must be under 2MB");
        e.target.value = "";
        return;
      }
    }

    setFile((prev) => [...prev, ...selectedFiles]);
    e.target.value = "";
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {

      let uploadedFile = "";

      if (file.length > 0) {
        uploadedFile = await uploadImage(file[0], token);
      }


      const payload = {
        standardId: formData.standardId,
        sectionId: formData.sectionId,
        subjectId: formData.subjectId,
        teacherId,
        dueDate: formData.dueDate,
        title: formData.title,
        description: formData.description,
        file: uploadedFile,
        assignDate: new Date().toISOString(),
      };

      await dispatch(addHomework(payload)).unwrap();
      toast.success("Homework added successfully");
      navigate("/teacher/homework");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to add homework",
      );
    }
  };

  return (
    <>

      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/teacher/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>

            <FiChevronRight className="separator" />

            <Link to="/teacher/homework">Homework</Link>

            <FiChevronRight className="separator" />

            <span className="active">Add Homework</span>
          </div>

        </div>
      </div>
      <div className="acs-card">
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={3}>
              <Form.Label>Standard</Form.Label>
              <Form.Select name="standardId"
                value={formData.standardId}
                onChange={handleStandardChange}
              >
                <option value="">Select Standard</option>
                {standards.map((std) => (
                  <option key={std._id} value={std._id}>
                    {std.name}
                  </option>
                ))}
              </Form.Select>

              {errors.standardId && (
                <small className="text-danger">{errors.standardId}</small>
              )}
            </Col>

            {/* Section */}
            <Col md={3}>
              <Form.Label>Section</Form.Label>
              <Form.Select name="sectionId"
                value={formData.sectionId}
                onChange={handleSectionChange}
                disabled={!sections.length}
              >
                <option value="">Select Section</option>
                {sections.map((sec) => (
                  <option key={sec._id} value={sec._id}>
                    {sec.name}
                  </option>
                ))}
              </Form.Select>

              {errors.sectionId && (
                <small className="text-danger">{errors.sectionId}</small>
              )}
            </Col>

            {/* Subject */}
            <Col md={3}>
              <Form.Label>Subject</Form.Label>
              <Form.Select
                name="subjectId"
                value={formData.subjectId}
                onChange={handleChange}
                disabled={!subjects.length}
              >
                <option value="">Select Subject</option>
                {subjects.map((sub) => (
                  <option key={sub._id} value={sub._id}>
                    {sub.name}
                  </option>
                ))}
              </Form.Select>

              {errors.subjectId && (
                <small className="text-danger">{errors.subjectId}</small>
              )}
            </Col>

            {/* Due Date */}
            <Col md={3}>
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                type="date"
                name="dueDate"
                min={new Date().toISOString().split("T")[0]}
                value={formData.dueDate}
                onChange={handleChange}
              />

              {errors.dueDate && (
                <small className="text-danger">{errors.dueDate}</small>
              )}
            </Col>

            {/* Title */}
            <Col md={4}>
              <Form.Label>Title</Form.Label>
              <Form.Control
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Homework title"
              />
              {errors.title && (
                <small className="text-danger">{errors.title}</small>
              )}
            </Col>


            <Col md={4}>
              <Form.Label>File</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}

              />



              <small className="text-muted">
                Only PDF / JPG / PNG allowed. Max size: 2MB.
              </small>
              {errors.file && (
                <div>
                  <small className="text-danger">{errors.file}</small>
                </div>
              )}
            </Col>
            <Col md={4}>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Homework description"
                style={{ height: "6rem", resize: "none" }}
              />
              {errors.description && (
                <small className="text-danger">{errors.description}</small>
              )}
            </Col>

          </Row>

          <div className="an-actions mt-5">
            <button
              type="button"
              className="btn-cancel"
              style={{ color: "black" }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              Submit
            </button>
          </div>
        </Form>
      </div>
    </>
  );
}
